/**
 * @file MiniRouteSync.tsx
 * @author ickeep
 * @description 路由同步组件，用于同步 MiniRouter 和 React Router 的路由状态
 * @since 0.0.1
 */

import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { MiniRouterContext } from '../context/MiniRouterContext';
import { useMiniRouterPath } from '../hooks/useMiniRouter';

/** getBrowserPath 读取浏览器当前包含查询参数和 hash 的完整路径。 */
const getBrowserPath = () => {
  if (typeof window === 'undefined') return '';
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
};

/** getFullPath 把 pathname、search 和 hash 组合成可直接比较的完整路径。 */
const getFullPath = (pathname = '', search = '', hash = '') => `${pathname}${search}${hash}`;

/**
 * MiniRouteSync 组件的属性接口
 * @interface MiniRouteSyncProps
 * @property {React.ReactNode} [children] - 子组件
 * @property {string} [basename] - 路由基础路径
 */
export const MiniRouteSync: React.FC<{ children?: React.ReactNode, basename?: string }> = ({ children, basename }) => {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const path = useMiniRouterPath();
  const miniContext = useContext(MiniRouterContext);
  const miniSearch = miniContext?.location.search ?? '';
  const miniHash = miniContext?.location.hash ?? '';
  const miniFullPath = useMemo(() => getFullPath(path, miniSearch, miniHash), [miniHash, miniSearch, path]);
  const prevPathRef = useRef(miniFullPath);
  const reactRouterFullPath = useMemo(
    () => (basename ? getFullPath(`${basename}${pathname}`, search, hash) : getFullPath(pathname, search, hash)),
    [basename, hash, pathname, search],
  );

  /**
   * React Router 主动跳转不会触发 popstate，因此浏览器地址正确但 MiniRouter 仍可能滞后。
   * 此时只从 window 同步 MiniRouter 状态，避免重复写入 history。
   */
  useEffect(() => {
    if (!basename || !miniContext?.syncPathFromWindow) return;
    if (miniFullPath === reactRouterFullPath) return;
    if (getBrowserPath() === reactRouterFullPath) {
      miniContext.syncPathFromWindow();
    }
  }, [basename, miniContext, miniFullPath, reactRouterFullPath]);

  /**
   * popstate 会让 MiniRouter 先于 React Router 更新；这里使用 replace 对齐内层路由，
   * 防止同一浏览器地址被再次 push，确保前进后退栈保持原有顺序。
   */
  useEffect(() => {
    if (!basename) return;
    if (prevPathRef.current === miniFullPath) return;

    if (getBrowserPath() === miniFullPath && reactRouterFullPath !== miniFullPath) {
      navigate(miniFullPath.replace(basename, '') || '/', { replace: true });
    }
    prevPathRef.current = miniFullPath;
  }, [basename, miniFullPath, navigate, reactRouterFullPath]);

  return <>{children}</>;
};
